import { Input, Tooltip, Form } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { CityAsyncSelect } from '@/components/pos/CityAsyncSelect';
import { WardAsyncSelect } from '@/components/pos/WardAsyncSelect';
import { FloatingField } from './FloatingField';
import { DateMaskInput } from './DateMaskInput';
import {
  getCitizenIdRules,
  customerTaxCodeRules,
  customerPhoneRules,
  customerEmailRules,
  customerBirthdayRules,
  requiredRule,
  isMaskedCccd,
} from './customerFormSchema';
import type { UserType } from '@/pages/POS/types';

interface Props {
  form: FormInstance;
  userType: UserType;
  cityId?: string;
  cccdMasked?: boolean;
  onRevealCccd?: () => void;
  personalCccdRequired?: boolean;
  /**
   * TCR-2511 — ghi đè trạng thái khoá riêng cho ô "Số điện thoại", giống `invoiceEmailDisabled`
   * của InvoiceSection: `undefined` = theo `ConfigProvider componentDisabled` bao ngoài, `false` =
   * mở ô này dù khối đang khoá (vai trò chỉ có `customers.phone.update`).
   */
  phoneDisabled?: boolean;
}

export function CustomerInfoFields({
  form,
  userType,
  cityId,
  cccdMasked,
  onRevealCccd,
  personalCccdRequired = true,
  phoneDisabled,
}: Props) {
  const isPersonal = userType === 'personal';
  const citizenIdVal = Form.useWatch('citizen_id', form);
  const isCccdMasked = cccdMasked || isMaskedCccd(citizenIdVal);

  const cccdSuffix =
    isCccdMasked && onRevealCccd ? (
      <Tooltip title="Nhấp để giải mã & sửa CCCD">
        <EyeOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={onRevealCccd} />
      </Tooltip>
    ) : undefined;

  return (
    <>
      <FloatingField
        label={isPersonal ? 'Tên khách hàng' : 'Tên công ty'}
        name="customer_name"
        rules={[requiredRule(isPersonal ? 'tên khách hàng' : 'tên công ty')]}
        required
      >
        <Input />
      </FloatingField>

      <FloatingField label="Số điện thoại" name="customer_phone" rules={customerPhoneRules} required>
        <Input maxLength={11} disabled={phoneDisabled} />
      </FloatingField>

      {isPersonal ? (
        <FloatingField
          label="CCCD"
          name="citizen_id"
          rules={getCitizenIdRules(personalCccdRequired)}
          required={personalCccdRequired}
        >
          <Input maxLength={12} suffix={cccdSuffix} />
        </FloatingField>
      ) : (
        <FloatingField label="Mã số thuế" name="customer_tax_code" rules={customerTaxCodeRules} required>
          <Input maxLength={14} />
        </FloatingField>
      )}

      <FloatingField label="Email" name="customer_email" rules={customerEmailRules}>
        <Input />
      </FloatingField>

      <FloatingField label="Thành phố" name="customer_city_id" rules={[requiredRule('tỉnh/thành phố')]} required>
        <CityAsyncSelect
          placeholder="Chọn thành phố"
          onChange={(v) => {
            form.setFieldValue('customer_city_id', v);
            form.setFieldsValue({ customer_ward_id: undefined });
          }}
          onChangeWithLabel={(_, name) => form.setFieldValue('_cityName', name)}
        />
      </FloatingField>

      <FloatingField label="Phường / xã" name="customer_ward_id" rules={[requiredRule('phường / xã')]} required>
        <WardAsyncSelect
          cityId={cityId ?? ''}
          placeholder="Chọn phường / xã"
          onChange={(v) => form.setFieldValue('customer_ward_id', v)}
          onChangeWithLabel={(_, name) => form.setFieldValue('_wardName', name)}
        />
      </FloatingField>

      <FloatingField label="Địa chỉ chi tiết" name="customer_address" rules={[requiredRule('địa chỉ chi tiết')]} required>
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </FloatingField>

      <FloatingField label="Ngày sinh" name="customer_birthday" rules={customerBirthdayRules}>
        <DateMaskInput />
      </FloatingField>

      <FloatingField label="Ghi chú" name="description" className="note-inline">
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </FloatingField>
    </>
  );
}
